import { useEffect } from 'react';
import * as PIXI from 'pixi.js';
import { getWallTexturePath, getFloorTexturePath } from '../../data/furniture';
import { applyRoomTextureScale, applyWallSpriteTexture, freeRoomTexture } from './render/textures';
import type { RenderFurniture } from '../../domain/types';
import type { RoomSceneState } from './state/roomSceneState';

// ── Wall/floor texture swap + initial backdrop catalog ids ──
// Extracted verbatim from RoomCanvas (decomposition final cleanup pass). Two effects,
// registered in their original relative order (initial-ids first, swap second), both
// still ahead of the scene-init effect (the hook is called before useRoomScene):
//  * initial-ids effect — keeps a ref of the active custom wall/floor catalog ids so
//    the scene-init effect can load the real backdrop directly;
//  * swap effect — swaps wall/floor textures when a walls/floors decoration item is
//    placed, releasing the previous textures to free GPU memory.
// The effect bodies are byte-identical to the originals; RoomCanvas-owned refs and
// state are received as parameters — the only mechanical rewrite applied.

export interface UseRoomTextureSwapParams {
  isSceneReady: boolean;
  isCityHall: boolean;
  placedFurniture: RenderFurniture[];
  sceneRef: React.MutableRefObject<RoomSceneState>;
}

export function useRoomTextureSwap({
  isSceneReady,
  isCityHall,
  placedFurniture,
  sceneRef,
}: UseRoomTextureSwapParams): void {
  // Keep a ref of the active custom wall/floor catalog ids so the scene-init effect
  // can load the real backdrop directly (avoids loading the large default 55/56 textures
  // and then swapping, which would leave duplicate textures resident — see ref declaration).
  // Declared before the scene-init effect so it runs first on the same commit.
  useEffect(() => {
    const activeWall =
      placedFurniture.find(f => f.is_preview && f.furniture_catalog?.category === 'walls') ??
      placedFurniture.find(f => f.furniture_catalog?.category === 'walls');
    const activeFloor =
      placedFurniture.find(f => f.is_preview && f.furniture_catalog?.category === 'floors') ??
      placedFurniture.find(f => f.furniture_catalog?.category === 'floors');
    sceneRef.current.backdrop.initialWallFloorCatalogIds = {
      wall: activeWall?.furniture_catalog_id ?? null,
      floor: activeFloor?.furniture_catalog_id ?? null,
    };
  }, [placedFurniture, sceneRef]);

  // Swap wall/floor textures when a walls/floors decoration item is placed
  useEffect(() => {
    if (!isSceneReady) return;
    let cancelled = false;

    // Preview items take priority over placed items for wall/floor textures
    const activeWall =
      placedFurniture.find(f => f.is_preview && f.furniture_catalog?.category === 'walls') ??
      placedFurniture.find(f => f.furniture_catalog?.category === 'walls');
    const activeFloor =
      placedFurniture.find(f => f.is_preview && f.furniture_catalog?.category === 'floors') ??
      placedFurniture.find(f => f.furniture_catalog?.category === 'floors');

    const wallCatId = activeWall?.furniture_catalog_id ?? null;
    const floorCatId = activeFloor?.furniture_catalog_id ?? null;

    // Swap wall textures if changed
    if (wallCatId !== sceneRef.current.backdrop.activeWallCatalogId) {
      const prevWallPath = getWallTexturePath(sceneRef.current.backdrop.activeWallCatalogId ?? (isCityHall ? 57 : 55));
      (async () => {
        try {
          const wallPath = wallCatId
            ? getWallTexturePath(wallCatId)
            : getWallTexturePath(isCityHall ? 57 : 55);
          const wallTex = await PIXI.Assets.load(wallPath);
          if (cancelled) return;
          sceneRef.current.backdrop.activeWallCatalogId = wallCatId;
          sceneRef.current.backdrop.loadedTexturePaths.add(wallPath);
          if (sceneRef.current.sprites.wallLeft && !sceneRef.current.sprites.wallLeft.destroyed) {
            applyWallSpriteTexture(sceneRef.current.sprites.wallLeft, wallTex, true);
          }
          if (sceneRef.current.sprites.wallRight && !sceneRef.current.sprites.wallRight.destroyed) {
            applyWallSpriteTexture(sceneRef.current.sprites.wallRight, wallTex, false);
          }
          // Release the previous wall texture so two large wall textures never stay resident.
          freeRoomTexture(prevWallPath, new Set([wallPath]), sceneRef.current.backdrop.loadedTexturePaths);
        } catch (e) {
          if (!cancelled) console.error('[RoomCanvas] Failed to load wall textures:', e);
        }
      })();
    }

    // Swap floor texture if changed
    if (floorCatId !== sceneRef.current.backdrop.activeFloorCatalogId) {
      const prevFloorPath = getFloorTexturePath(sceneRef.current.backdrop.activeFloorCatalogId ?? (isCityHall ? 58 : 56));
      (async () => {
        try {
          const floorPath = floorCatId
            ? getFloorTexturePath(floorCatId)
            : getFloorTexturePath(isCityHall ? 58 : 56);
          const tex = await PIXI.Assets.load(floorPath);
          if (cancelled) return;
          sceneRef.current.backdrop.activeFloorCatalogId = floorCatId;
          sceneRef.current.backdrop.loadedTexturePaths.add(floorPath);
          if (sceneRef.current.sprites.floor && !sceneRef.current.sprites.floor.destroyed) {
            sceneRef.current.sprites.floor.texture = tex;
            applyRoomTextureScale(sceneRef.current.sprites.floor, tex);
          }
          // Release the previous floor texture (e.g. the 4000x2000 default) to free GPU memory.
          freeRoomTexture(prevFloorPath, new Set([floorPath]), sceneRef.current.backdrop.loadedTexturePaths);
        } catch (e) {
          if (!cancelled) console.error('[RoomCanvas] Failed to load floor texture:', e);
        }
      })();
    }

    return () => { cancelled = true; };
  }, [placedFurniture, isSceneReady, isCityHall, sceneRef]);
}
